import type { MortgageData } from '@/components/calculator/CalculatorTabs';
import { formatUserName, getUserEmail } from '@/utils/userProfileUtils';

export interface TransformedReportData {
  formData: {
    primaryIncome?: {
      annualIncome: number;
    };
    personalDetails?: {
      creditScore?: number;
      state?: string;
      veteranStatus?: string;
    };
    propertyLoan?: {
      loanType: string;
      downPaymentPercentage: number;
      interestRate: number;
      loanTerm: number;
    };
    propertyDetails?: {
      homePrice: number;
      propertyType?: string;
      city?: string;
      state?: string;
    };
    monthlyPayment?: {
      principal: number;
      interest: number;
      taxes: number;
      insurance: number;
      pmi: number;
      hoa: number;
      total: number;
    };
    closingCosts?: {
      total: number;
      breakdown: Record<string, number>;
    };
  };
  results: {
    maxPurchasePrice: number;
    maxMonthlyPayment: number;
    debtToIncomeRatio: number;
    loanToValue: number;
    totalInterest: number;
    totalCost: number;
    netClosingCosts: number;
    totalCashAtClosing: number;
    totalAmountPaid: number;
    loanAmount: number;
    keyMetrics?: {
      monthlyPayment: number;
      totalInterest: number;
      netClosingCosts: number;
      cashToClose: number;
    };
    monthlyBreakdown?: {
      principalAndInterest: number;
      propertyTaxes: number;
      homeInsurance: number;
      pmi: number;
      hoaFees: number;
    };
    loanAnalysis?: {
      loanAmount: number;
      loanType: string;
      interestRate: number;
      loanTerm: number;
      loanToValue: number;
      totalAmountPaid: number;
    };
  };
  analytics: {
    riskAssessment: {
      riskLevel: string;
      loanToValue: number;
      downPaymentRatio: number;
      closingCostRatio: number;
    };
    costAnalysis: {
      homePrice: number;
      totalInterest: number;
      closingCosts: number;
      totalCostOfOwnership: number;
      interestToLoanRatio: number;
    };
    incomeRequirements: {
      recommendedAnnualIncome: number;
      recommendedMonthlyIncome: number;
      currentMonthlyPayment: number;
      debtToIncomeRatio: number;
    };
    paymentComposition: {
      year1Principal: number;
      year1Interest: number;
      totalPrincipal: number;
      totalInterest: number;
      interestToPrincipalRatio: number;
    };
    keyInsights: Array<{
      category: string;
      status: 'good' | 'warning' | 'concern';
      title: string;
      message: string;
    }>;
  };
  supremeIntelligence: {
    availablePrograms: Array<{
      id: string;
      name: string;
      monthlyPayment: number;
      downPayment: number;
      downPercent: number;
      apr: number;
      totalCost: number;
      savings: number;
      recommended: boolean;
      eligible: boolean;
    }>;
    optimization: {
      extraPrincipalSavings: number;
      refinancingOpportunity: number;
      shortTermStrategies: string[];
      longTermStrategies: string[];
    };
    analytics: {
      bestMonthlyPayment: number;
      programsAvailable: number;
      totalPotentialSavings: number;
    };
  };
  eligibilityResults?: Record<string, any>;
  comparisonResults?: any[];
  userInfo?: {
    name?: string;
    email?: string;
  };
  completionStatus?: {
    isComplete: boolean;
    hasCalculation: boolean;
  };
}

export const transformMortgageDataToReportData = (
  mortgageData: MortgageData,
  user?: any
): TransformedReportData => {
  // Calculate derived values to match Enhanced Results Summary
  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
  const totalInterest = ((mortgageData.monthlyPayment.principal + mortgageData.monthlyPayment.interest) * mortgageData.loanTerm * 12) - loanAmount;
  const netClosingCosts = mortgageData.closingCosts.total - (mortgageData.closingCosts.sellerCreditAmount || 0);
  const totalCashAtClosing = mortgageData.closingCosts.totalCashNeeded || (mortgageData.downPayment + netClosingCosts);
  const totalCost = mortgageData.homePrice + netClosingCosts + totalInterest;
  const loanToValue = (loanAmount / mortgageData.homePrice) * 100;
  const debtToIncomeEstimate = (mortgageData.monthlyPayment.total / (mortgageData.homePrice * 0.0004)) * 100;
  
  // Enhanced Results Summary specific calculations
  const totalAmountPaid = (mortgageData.monthlyPayment.principal + mortgageData.monthlyPayment.interest) * mortgageData.loanTerm * 12;

  // Analytics calculations
  const interestToLoanRatio = loanAmount > 0 ? (totalInterest / loanAmount) * 100 : 0;
  const closingCostRatio = mortgageData.homePrice > 0 ? (mortgageData.closingCosts.total / mortgageData.homePrice) * 100 : 0;
  const monthlyIncomeNeeded = mortgageData.monthlyPayment.total > 0 ? mortgageData.monthlyPayment.total / 0.28 : 0;
  const annualIncomeNeeded = monthlyIncomeNeeded * 12;

  // Risk assessment
  const getRiskLevel = () => {
    if (loanToValue > 95) return 'High';
    if (loanToValue > 80) return 'Medium';
    return 'Low';
  };

  // Generate key insights
  const keyInsights = [
    {
      category: 'PMI Status',
      status: loanToValue <= 80 ? 'good' as const : 'warning' as const,
      title: 'PMI Status',
      message: loanToValue <= 80 ? 'No PMI required' : 'PMI required until 80% LTV'
    },
    {
      category: 'Down Payment',
      status: mortgageData.downPaymentPercentage >= 20 ? 'good' as const : 'warning' as const,
      title: 'Down Payment',
      message: mortgageData.downPaymentPercentage >= 20 ? 'Excellent down payment' : 'Consider increasing if possible'
    },
    {
      category: 'Interest Cost',
      status: interestToLoanRatio <= 50 ? 'good' as const : 'concern' as const,
      title: 'Interest Cost',
      message: interestToLoanRatio <= 50 ? 'Reasonable interest cost' : 'Consider shorter term or better rate'
    },
    {
      category: 'Closing Costs',
      status: closingCostRatio <= 3 ? 'good' as const : 'warning' as const,
      title: 'Closing Costs',
      message: closingCostRatio <= 3 ? 'Normal closing costs' : 'Higher than average closing costs'
    }
  ];

  // Mock loan programs (in real implementation, these would come from actual data)
  const mockLoanPrograms = [
    {
      id: 'conv-30',
      name: 'Conventional 30-Year',
      monthlyPayment: mortgageData.monthlyPayment.total,
      downPayment: mortgageData.downPayment,
      downPercent: mortgageData.downPaymentPercentage,
      apr: mortgageData.interestRate,
      totalCost: totalCost,
      savings: 0,
      recommended: true,
      eligible: true
    },
    {
      id: 'fha-30',
      name: 'FHA 30-Year',
      monthlyPayment: mortgageData.monthlyPayment.total * 0.95,
      downPayment: mortgageData.homePrice * 0.035,
      downPercent: 3.5,
      apr: mortgageData.interestRate + 0.25,
      totalCost: totalCost * 0.98,
      savings: totalCost * 0.02,
      recommended: false,
      eligible: true
    },
    {
      id: 'va-30',
      name: 'VA 30-Year',
      monthlyPayment: mortgageData.monthlyPayment.total * 0.92,
      downPayment: 0,
      downPercent: 0,
      apr: mortgageData.interestRate - 0.25,
      totalCost: totalCost * 0.95,
      savings: totalCost * 0.05,
      recommended: false,
      eligible: mortgageData.loanType === 'va'
    }
  ];

  return {
    formData: {
      primaryIncome: {
        annualIncome: mortgageData.homePrice * 0.3 // Estimate based on home price
      },
      personalDetails: {
        creditScore: mortgageData.creditScore,
        state: mortgageData.location?.state,
        veteranStatus: mortgageData.isVeteran ? 'Yes' : 'No'
      },
      propertyLoan: {
        loanType: mortgageData.loanType,
        downPaymentPercentage: mortgageData.downPaymentPercentage,
        interestRate: mortgageData.interestRate,
        loanTerm: mortgageData.loanTerm
      },
      propertyDetails: {
        homePrice: mortgageData.homePrice,
        propertyType: mortgageData.propertyType,
        city: mortgageData.location?.city,
        state: mortgageData.location?.state
      },
      monthlyPayment: mortgageData.monthlyPayment,
      closingCosts: {
        total: mortgageData.closingCosts.total,
        breakdown: {
          loanOrigination: mortgageData.closingCosts.loanOrigination,
          appraisalFee: mortgageData.closingCosts.appraisalFee,
          titleServices: mortgageData.closingCosts.titleServices,
          governmentRecordingCharges: mortgageData.closingCosts.governmentRecordingCharges,
          prepaids: mortgageData.closingCosts.prepaids,
          initialEscrow: mortgageData.closingCosts.initialEscrow,
          other: mortgageData.closingCosts.other,
          fundingFee: mortgageData.closingCosts.fundingFee,
          sellerCreditAmount: mortgageData.closingCosts.sellerCreditAmount
        }
      }
    },
    results: {
      maxPurchasePrice: mortgageData.homePrice,
      maxMonthlyPayment: mortgageData.monthlyPayment.total,
      debtToIncomeRatio: debtToIncomeEstimate,
      loanToValue,
      totalInterest,
      totalCost,
      netClosingCosts,
      totalCashAtClosing,
      totalAmountPaid,
      loanAmount,
      // Enhanced Results Summary key metrics
      keyMetrics: {
        monthlyPayment: mortgageData.monthlyPayment.total,
        totalInterest,
        netClosingCosts,
        cashToClose: totalCashAtClosing
      },
      // Monthly payment breakdown for visualization
      monthlyBreakdown: {
        principalAndInterest: mortgageData.monthlyPayment.principal + mortgageData.monthlyPayment.interest,
        propertyTaxes: mortgageData.monthlyPayment.taxes,
        homeInsurance: mortgageData.monthlyPayment.insurance,
        pmi: mortgageData.monthlyPayment.pmi,
        hoaFees: mortgageData.monthlyPayment.hoa
      },
      // Loan analysis details
      loanAnalysis: {
        loanAmount,
        loanType: mortgageData.loanType,
        interestRate: mortgageData.interestRate,
        loanTerm: mortgageData.loanTerm,
        loanToValue,
        totalAmountPaid
      }
    },
    analytics: {
      riskAssessment: {
        riskLevel: getRiskLevel(),
        loanToValue: loanToValue,
        downPaymentRatio: mortgageData.downPaymentPercentage,
        closingCostRatio: closingCostRatio
      },
      costAnalysis: {
        homePrice: mortgageData.homePrice,
        totalInterest: totalInterest,
        closingCosts: mortgageData.closingCosts.total,
        totalCostOfOwnership: totalCost,
        interestToLoanRatio: interestToLoanRatio
      },
      incomeRequirements: {
        recommendedAnnualIncome: annualIncomeNeeded,
        recommendedMonthlyIncome: monthlyIncomeNeeded,
        currentMonthlyPayment: mortgageData.monthlyPayment.total,
        debtToIncomeRatio: 28 // Based on 28% DTI rule
      },
      paymentComposition: {
        year1Principal: mortgageData.monthlyPayment.principal * 12,
        year1Interest: mortgageData.monthlyPayment.interest * 12,
        totalPrincipal: loanAmount,
        totalInterest: totalInterest,
        interestToPrincipalRatio: loanAmount > 0 ? totalInterest / loanAmount : 0
      },
      keyInsights: keyInsights
    },
    supremeIntelligence: {
      availablePrograms: mockLoanPrograms,
      optimization: {
        extraPrincipalSavings: Math.round(totalInterest * 0.15), // Estimated savings from extra payments
        refinancingOpportunity: Math.round(mortgageData.monthlyPayment.total * 0.1), // Estimated monthly savings from 0.5% rate reduction
        shortTermStrategies: [
          'Shop for better insurance rates quarterly',
          'Consider bi-weekly payment schedule',
          'Monitor credit score improvements',
          'Research property tax appeals'
        ],
        longTermStrategies: [
          'Plan for refinancing opportunities',
          'Build equity through improvements',
          'Consider investment property expansion',
          'Optimize tax deduction strategies'
        ]
      },
      analytics: {
        bestMonthlyPayment: Math.min(...mockLoanPrograms.filter(p => p.eligible).map(p => p.monthlyPayment)),
        programsAvailable: mockLoanPrograms.filter(p => p.eligible).length,
        totalPotentialSavings: mockLoanPrograms.reduce((sum, program) => sum + program.savings, 0)
      }
    },
    userInfo: user ? {
      name: formatUserName(user),
      email: getUserEmail(user)
    } : undefined,
    completionStatus: {
      isComplete: mortgageData.monthlyPayment.total > 0 && mortgageData.closingCosts.total > 0,
      hasCalculation: mortgageData.monthlyPayment.total > 0
    }
  };
};